// helix-exception: Helix has no semantic token for these hues (Figma needs-input orange, highlight yellow/amber, categorical teal/cyan); primitive ramps with the identical colour are used (F-11)
import { AlertBanner } from '@/components/common/AlertBanner'
import { InfoCircle } from 'flowbite-react-icons/outline'

/**
 * Guidance disposition for an order whose genomic report is ready.
 *
 * - `'pending'`:     guidance has not been finalized yet, but still may be.
 * - `'unavailable'`: the lab resolved this result without guidance; none is coming.
 * - `'none'`:        nothing to announce (guidance is final, so the Guidance
 *                    section is shown instead of a status banner).
 */
export type GuidanceNotice = 'none' | 'unavailable' | 'pending'

/**
 * Status banner for the results screen when there is no Guidance section to show.
 *
 * The two states are deliberately given different colour and wording: "pending"
 * is a wait, "unavailable" is a final answer, and a provider who cannot tell
 * them apart does not know whether to keep checking back. Neither message
 * explains the lab's internal rationale for withholding guidance — the genomic
 * report is the clinical artifact in both cases, so both point there.
 */
export function GuidanceNoticeBanner({ notice }: { notice: Exclude<GuidanceNotice, 'none'> }) {
  if (notice === 'unavailable') {
    return (
      <div
        className="flex w-full gap-4 rounded-base border border-warning-300 dark:border-warning-600 bg-warning-50 dark:bg-gray-900 p-4"
        role="status"
        aria-live="polite"
      >
        <InfoCircle className="mt-0.5 h-5 w-5 shrink-0 text-orange-900 dark:text-orange-200" />
        <p className="min-w-0 flex-1 whitespace-normal break-words text-sm font-medium leading-5 text-orange-900 dark:text-orange-200">
          <span className="font-semibold">Guidance is not available for this result.</span> Refer to
          the genomic report for laboratory findings and interpretation.
        </p>
      </div>
    )
  }

  return (
    <AlertBanner useInflectionIcon={true}>
      <span className="font-semibold">Guidance is not yet available.</span> The genomic report is
      available for review. Guidance may be provided once clinical review is complete.
    </AlertBanner>
  )
}

export default GuidanceNoticeBanner
